import type { ReactElement } from 'react';
import React, { useId, useMemo } from 'react';
import { faArrowDownShortWide, faArrowUpWideShort } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormGroup } from 'reactstrap';
import { BooleanParam, createEnumParam, useQueryParam } from 'use-query-params';
import Label from 'atom-components/label';
import Select from 'atom-components/select/select';

export type SortTypes = 'encounters' | 'percentageCorrect' | 'difficulty';
export type Sort = { type: SortTypes; asc: boolean };

const sortOptions: readonly { id: SortTypes; label: string }[] = [
  { id: 'encounters', label: 'Number of encounters' },
  { id: 'percentageCorrect', label: 'Percentage correct' },
  { id: 'difficulty', label: 'Item difficulty' }
];

const defaultOption = sortOptions[0];

const SortTypeParam = createEnumParam(sortOptions.map((o) => o.id));

export function useSortQueryParams(): Sort {
  const [sortDESC] = useQueryParam('sort-desc', BooleanParam);
  const [sortType] = useQueryParam('sort-type', SortTypeParam);

  return useMemo(
    () => ({ type: sortType ?? defaultOption.id, asc: !sortDESC }),
    [sortDESC, sortType]
  );
}

type Props = { label: string };

export default function SortSelector({ label }: Props): ReactElement {
  const inputId = useId();

  const [sortDESC, setSortDESC] = useQueryParam('sort-desc', BooleanParam);
  const [sortType, setSortType] = useQueryParam('sort-type', SortTypeParam);
  const value = useMemo(
    () => sortOptions.find((option) => option.id === sortType) ?? defaultOption,
    [sortType]
  );

  return (
    <div>
      <Label className="fw-bold" htmlFor={inputId}>
        {label}
      </Label>
      <FormGroup className="d-flex">
        <Select
          inputId={inputId}
          options={sortOptions}
          value={value}
          getOptionValue={(o): string => o.id}
          onChange={(item): void => setSortType(item?.id, 'replaceIn')}
          className=""
          isMulti={false}
        />
        <button
          type="button"
          className="btn btn-light py-1 px-2 ms-1"
          onClick={(): void => setSortDESC(!sortDESC, 'replaceIn')}
          aria-label="Sort descending"
          aria-pressed={!!sortDESC}
        >
          <FontAwesomeIcon
            icon={sortDESC ? faArrowDownShortWide : faArrowUpWideShort}
            rotation={90}
          />
        </button>
      </FormGroup>
    </div>
  );
}
